import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

/**
 * Fetches standings for a given season from the `standings` view.
 * Returns rows ordered by rank ASC (the view handles sorting).
 *
 * Row shape:
 *   { id, season_id, name, logo_url, rank, wins, losses,
 *     games_played, win_pct, point_diff, games_behind, streak }
 *
 * `streak` is a string like 'WWLWL' (most recent first).
 * Split it with streak.split('') for the dot display.
 */
export const useStandings = (seasonId) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!seasonId) {
      setData([]);
      setLoading(false);
      return;
    }

    const fetchStandings = async () => {
      setLoading(true);
      setError(null);

      const { data: rows, error: err } = await supabase
        .from("standings")
        .select(
          `
            id, season_id, name, logo_url, rank, wins, losses,
            games_played, win_pct, point_diff, games_behind, streak,
            team_profiles (gradient, primary_color, secondary_color)
        `,
        )
        .eq("season_id", seasonId)
        .order("rank", { ascending: true });

      if (err) {
        setError(err.message);
        setData([]);
        setLoading(false);
        return;
      }

      // Filter out All-Star teams by checking the teams table
      // (standings view doesn't include is_all_star_team column)
      if (rows && rows.length > 0) {
        const teamIds = rows.map((r) => r.id);
        const { data: teams } = await supabase
          .from("teams")
          .select("id, is_all_star_team")
          .in("id", teamIds);

        const allStarTeamIds = new Set(
          teams?.filter((t) => t.is_all_star_team === true).map((t) => t.id) ||
            [],
        );

        // Filter out All-Star teams
        const filtered = rows.filter((row) => !allStarTeamIds.has(row.id));
        setData(filtered);
      } else {
        setData(rows ?? []);
      }

      setLoading(false);
    };

    fetchStandings();
  }, [seasonId]);

  return { data, loading, error };
};
